/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import {
	Component,
	AfterViewInit,
	ChangeDetectionStrategy,
	ViewChild,
	OnDestroy,
	ChangeDetectorRef,
	OnInit,
} from '@angular/core';
import { PlanState, StateEvents } from 'src/app/services/plan-state.service';
import {
	CrewSizeApplication,
	CREW_SIZE_SETTINGS,
} from 'src/app/plain-javascript/crew-sizes/CrewSizeApplication';
import { takeUntil, distinctUntilChanged, map } from 'rxjs/operators';
import { Subject, BehaviorSubject } from 'rxjs';
import { CREW_SIZES_CLOSED_MESSAGE, CREW_SIZES_ZOOM_IN_MESSAGE } from '@touchplan/strings';
import { settings } from 'pixi.js-legacy';
import { analyticsEventsService } from 'src/app/plain-javascript/services/analytics/AnalyticsEventsService';
import { UserPreferenceStateService } from '../../../services/user-preference-state.service';
import { combineLatest } from 'rxjs';
import { ViewportChangedEvent } from '@appCanvasCommon/interfaces/camera.interface';
import { IMISCProjectPreferencesStateService } from 'src/app/services/project-misc-preference-state.service';
import { Role } from '@touchplan/common';
import { CrewSizeService } from '../services/crew-size.service';
import { ICrewSizeApplicationDependencies } from '../../../app-data/interfaces/crew-size-dependencies';
import { CameraService } from '../services/camera.service';
import { ActiveColumnService } from '../services/active-column.service';

@Component({
	selector: 'crew-sizes',
	templateUrl: './crew-sizes.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CrewSizesComponent implements AfterViewInit, OnDestroy {
	@ViewChild('canvasChart') public canvasChart;
	@ViewChild('container') public container;

	public selectedRoleFilter: Role[] = null;

	public zoomInMessage: string = CREW_SIZES_ZOOM_IN_MESSAGE;
	public zoomtextHidden$: BehaviorSubject<boolean> = new BehaviorSubject(true);
	public isHidden$: BehaviorSubject<boolean> = new BehaviorSubject(true);

	public shutdownSubject$: Subject<boolean> = new Subject();
	public application: CrewSizeApplication;
	public selectedRolesIds$ = this.miscProjectPreferencesStateService.crewSizeRoles$.pipe(
		map((serializedListPreferences) =>
			IMISCProjectPreferencesStateService.deserializeListPreferences(serializedListPreferences)
		)
	);

	private _lastShow: boolean = null;
	private showLaborCount$ = this.userPreferenceStateService.showLaborCount$;
	private crewSizesEnabled$ = this.userPreferenceStateService.crewSizesEnabled$;

	constructor(
		public planState: PlanState,
		private cd: ChangeDetectorRef,
		private userPreferenceStateService: UserPreferenceStateService,
		private miscProjectPreferencesStateService: IMISCProjectPreferencesStateService,
		private _crewSizeService: CrewSizeService,
		private _cameraService: CameraService,
		private _activeColumnService: ActiveColumnService
	) {
		this.selectedRoleFilter = null;
	}

	private assembleDependencies(): ICrewSizeApplicationDependencies {
		return {
			cameraService: this._cameraService,
			activeColumnService: this._activeColumnService,
			laborCounts: this.planState.laborCounts,
			projectLaborCounts: this.planState.projectLaborCounts,
			authedProjectMember$: this.planState.authedProjectMember$,
			rolesApi: this.planState.rolesApi,
		};
	}

	public filterChanged(newValue: Role[]): void {
		this.selectedRoleFilter = newValue;
		const rolesAsString = IMISCProjectPreferencesStateService.serializeListPreferences(
			newValue.map((role) => role.$id)
		);
		this.miscProjectPreferencesStateService.crewSizeRoles = rolesAsString;
		if (this.application) {
			analyticsEventsService.setCrewSizesFilter(this.planState.planId, rolesAsString);
			this.application.updateFilter(this.selectedRoleFilter);
		}
	}

	public ngAfterViewInit() {
		const unsub = this.planState.stateChange$.subscribe((stateEvent: StateEvents) => {
			if (stateEvent === StateEvents.FULLY_LOADED) {
				this.setup();
			}

			if (stateEvent === StateEvents.BOOMED) {
				unsub.unsubscribe();
			}
		});
	}

	public ngOnDestroy() {
		this.shutdownSubject$.next(true);
		if (this.application) {
			this.application.destroy();
		}
	}

	public setup(): void {
		// Initialize the labour count render
		this.application = new CrewSizeApplication(this.assembleDependencies(), {
			width: window.innerWidth,
			height: CREW_SIZE_SETTINGS.HEIGHT,
			view: this.canvasChart.nativeElement,
			resolution: window.devicePixelRatio && window.devicePixelRatio >= 1 ? window.devicePixelRatio : 1,
			autoResize: false,
			backgroundAlpha: 0,
			premultipliedAlpha: true,
			hello: true,
		});

		this._crewSizeService.registerPlanData(this.application);

		settings.ROUND_PIXELS = true;

		// Watch the plan camera for changes. Do this so we can move the view labor container around to accomodate space for DOW text
		this.planState.canvasConnector.camera.changed$
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe((newValues: ViewportChangedEvent) => {
				// This makes the crew sizes renderer 'responsive'
				if (this.application && newValues.scale.x <= CREW_SIZE_SETTINGS.SCALE_FOR_DAYS) {
					this.application.drawDays = false;
				} else if (this.application) {
					this.application.drawDays = true;
				}

				if (newValues.scale.x <= CREW_SIZE_SETTINGS.MAX_ZOOM_LEVEL) {
					this.zoomtextHidden$.next(false);
				} else {
					this.zoomtextHidden$.next(true);
				}
			});

		/**
		 * We manually trigger change detection here because we have blocked scroll events from Zone for performance reasons
		 * If this was not here, then the Zoom message would be delayed until the next change detection since it will fire outside of the
		 * Angular zone
		 */
		this.zoomtextHidden$.pipe(distinctUntilChanged(), takeUntil(this.shutdownSubject$)).subscribe(() => {
			// Plan camera LOD has changed. Manually call change detection, otherwise there will be a delay
			this.cd.detectChanges();
		});

		combineLatest([this.showLaborCount$, this.crewSizesEnabled$])
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe(([showLaborCount, crewSizesEnabled]) => {
				// Crew Sizes must be enabled on project or server AND user must have crew sizes showing
				if (crewSizesEnabled && showLaborCount) {
					this.isHidden$.next(false);
					this.application.active = showLaborCount;
					this.application.update(this.application._cachedDays, true);
					if (crewSizesEnabled !== this._lastShow) {
						analyticsEventsService.showCrewSizes(this.planState.planId, this.selectedRoleFilter?.join(','));
						this._lastShow = crewSizesEnabled;
					}
					// MOC-3189
					this.saveHiddenPreferences(true);
				} else if (this.application) {
					this.isHidden$.next(true);
					this.application.active = showLaborCount;
					// MOC-3189 - Only turn this off if project has crewSizes enabled
					if (crewSizesEnabled) {
						this.saveHiddenPreferences(false);
					}
				}
			});
	}

	public closeClicked(): void {
		Logging.notice(CREW_SIZES_CLOSED_MESSAGE);
		this.userPreferenceStateService.showLaborCount = false;
		this.saveHiddenPreferences(false);
	}

	private saveHiddenPreferences(isVisible: boolean): void {
		this.userPreferenceStateService.showLaborCount = isVisible;
	}
}
